/**
 * CloudWatch RUM (Real User Monitoring) initialization. Folded in from the
 * standalone `@juiceco-io/rum-init` package — kept as a separate file so the
 * server-side `index.ts` doesn't pull `aws-rum-web` types into its bundle.
 *
 * `aws-rum-web` is loaded via dynamic import on first init so the package
 * isn't required when RUM is disabled (e.g. SSR contexts).
 */

import type { AwsRum } from "aws-rum-web";

export interface RumConfig {
  appMonitorId?: string;
  identityPoolId?: string;
  guestRoleArn?: string;
  region?: string;
  appVersion?: string;
  sessionSampleRate?: number;
  enableXRay?: boolean;
  telemetries?: ("performance" | "errors" | "http")[];
  endpoint?: string;
}

let initialized = false;
let client: AwsRum | undefined;

/**
 * Initialize CloudWatch RUM.
 *
 * Reads configuration from environment variables by default:
 *   NEXT_PUBLIC_RUM_APP_MONITOR_ID
 *   NEXT_PUBLIC_RUM_IDENTITY_POOL_ID
 *   NEXT_PUBLIC_RUM_GUEST_ROLE_ARN
 *   NEXT_PUBLIC_RUM_REGION          (default: us-east-1)
 *   NEXT_PUBLIC_APP_VERSION          (default: dev)
 *
 * Returns silently if required env vars are missing (graceful degradation).
 */
export async function initRum(config?: RumConfig): Promise<void> {
  if (typeof window === "undefined" || initialized) return;

  const appMonitorId =
    config?.appMonitorId ?? process.env.NEXT_PUBLIC_RUM_APP_MONITOR_ID;
  const identityPoolId =
    config?.identityPoolId ?? process.env.NEXT_PUBLIC_RUM_IDENTITY_POOL_ID;
  const region =
    config?.region ?? process.env.NEXT_PUBLIC_RUM_REGION ?? "us-east-1";
  const appVersion =
    config?.appVersion ?? process.env.NEXT_PUBLIC_APP_VERSION ?? "dev";

  if (!appMonitorId || !identityPoolId) return;

  try {
    const { AwsRum: AwsRumCtor } = await import("aws-rum-web");

    if (initialized) return;

    // Use Cognito Identity Pool enhanced flow: passing identityPoolId alone
    // causes aws-rum-web to call cognito-identity:GetCredentialsForIdentity,
    // which resolves the unauthenticated role from the pool's role attachment.
    // Passing guestRoleArn here would switch the SDK to the classic/basic flow
    // (AssumeRoleWithWebIdentity), which the pool does not enable. guestRoleArn
    // is accepted as config input for forward-compat but intentionally not
    // forwarded to the SDK.
    client = new AwsRumCtor(appMonitorId, appVersion, region, {
      allowCookies: true,
      enableXRay: config?.enableXRay ?? false,
      telemetries: config?.telemetries ?? ["performance", "errors", "http"],
      sessionSampleRate: config?.sessionSampleRate ?? 1.0,
      identityPoolId,
      endpoint:
        config?.endpoint ?? `https://dataplane.rum.${region}.amazonaws.com`,
    });

    initialized = true;
  } catch (error) {
    console.error(
      "[@juiceco-io/observability-sdk] Failed to initialize CloudWatch RUM",
      error,
    );
  }
}

/** True when {@link initRum} has successfully initialized the AwsRum client. */
export function isRumInitialized(): boolean {
  return initialized;
}

/**
 * Returns the underlying `AwsRum` client when RUM is initialized, or
 * `undefined` otherwise. Use this to call methods like `recordError`,
 * `recordEvent`, or `addSessionAttributes` directly from app code or
 * other SDK helpers (fetch wrapper, error boundary).
 */
export function getRumClient(): AwsRum | undefined {
  return client;
}

/** @internal — for tests only. */
export function _resetRumForTest(): void {
  initialized = false;
  client = undefined;
}
